import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import FreeBreakfastIcon from '@mui/icons-material/FreeBreakfast';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PublicIcon from '@mui/icons-material/Public';
import { attendanceWebApi } from './attendanceWebApi';
import WebRegularizationModal from './WebRegularizationModal';

const CARD_SX = {
  p: { xs: 2.5, md: 3 },
  borderRadius: '20px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};

function generateUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function formatDuration(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const hrs = Math.floor(s / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

export default function WebClockCard({ onActionComplete }) {
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [regModalOpen, setRegModalOpen] = useState(false);
  const [localElapsed, setLocalElapsed] = useState(0);
  const [localActiveBreak, setLocalActiveBreak] = useState(0);

  const timerRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await attendanceWebApi.status();
      setStatusData(data);
      if (data?.session) {
        setLocalElapsed(data.session.elapsed_seconds || 0);
        setLocalActiveBreak(data.session.active_break_seconds || 0);
      }
    } catch (err) {
      console.error('Failed to fetch web attendance status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // Running second counter when session is active
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (statusData?.session && !statusData.session.closed_at) {
      timerRef.current = setInterval(() => {
        setLocalElapsed((prev) => prev + 1);
        if (statusData.session.active_break) {
          setLocalActiveBreak((prev) => prev + 1);
        }
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [statusData?.session]);

  const handlePunch = async (actionType) => {
    setErrorMsg('');
    setActionLoading(true);
    const reqId = generateUuid();
    try {
      if (actionType === 'clock-in') {
        await attendanceWebApi.clockIn(reqId);
      } else if (actionType === 'clock-out') {
        await attendanceWebApi.clockOut(reqId);
      } else if (actionType === 'break-start') {
        await attendanceWebApi.breakStart(reqId);
      } else if (actionType === 'break-end') {
        await attendanceWebApi.breakEnd(reqId);
      }
      await fetchStatus();
      if (onActionComplete) onActionComplete();
    } catch (err) {
      const data = err.response?.data;
      const code = data?.code || data?.detail || err.message;
      if (code === 'ACTIVE_BREAK') {
        setErrorMsg('You have an active break. You must end your break before clocking out.');
      } else if (code === 'OPEN_SESSION_EXISTS') {
        setErrorMsg('You already have an open attendance session in progress.');
      } else if (code === 'NO_ACTIVE_BREAK') {
        setErrorMsg('No active break was found to end.');
      } else if (code === 'EMPLOYEE_INACTIVE') {
        setErrorMsg('Inactive employees cannot record web attendance.');
      } else if (code === 'WEB_ATTENDANCE_DISABLED') {
        setErrorMsg('Web attendance is currently disabled.');
      } else {
        setErrorMsg(`Action failed: ${code}`);
      }
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Paper elevation={0} sx={{ ...CARD_SX, mb: 3, textAlign: 'center', py: 4 }}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Checking attendance status…
        </Typography>
      </Paper>
    );
  }

  if (!statusData?.enabled) {
    return (
      <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <AccessTimeIcon color="action" />
          <Typography variant="body2" color="text.secondary">
            Web Attendance is currently disabled for this tenant. Biometric attendance and roster policies remain active.
          </Typography>
        </Stack>
      </Paper>
    );
  }

  const session = statusData.session;
  const isClockedIn = Boolean(session && !session.closed_at);
  const isOnBreak = Boolean(session?.active_break);
  const netWorkingSeconds = Math.max(
    0,
    localElapsed - (session?.completed_break_seconds || 0) - (isOnBreak ? localActiveBreak : 0)
  );

  return (
    <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
      {/* Header row */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827' }}>
              Web Attendance
            </Typography>
            {isClockedIn ? (
              isOnBreak ? (
                <Chip
                  label="On Break"
                  size="small"
                  sx={{ bgcolor: '#FEF3C7', color: '#D97706', fontWeight: 700 }}
                />
              ) : (
                <Chip
                  label="Clocked In"
                  size="small"
                  sx={{ bgcolor: '#ECFDF5', color: '#059669', fontWeight: 700 }}
                />
              )
            ) : (
              <Chip label="Clocked Out" size="small" sx={{ bgcolor: '#F3F4F6', color: '#6B7280', fontWeight: 600 }} />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <PublicIcon sx={{ fontSize: 13 }} />
            Timezone: {statusData.timezone || 'Asia/Kolkata'} · Geofencing: {statusData.geofencing_enforced ? 'Enforced' : 'Off'}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<EditNoteIcon />}
          onClick={() => setRegModalOpen(true)}
          sx={{ textTransform: 'none', borderRadius: '10px', borderColor: '#e5e7eb', color: '#374151' }}
        >
          Regularize
        </Button>
      </Stack>

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setErrorMsg('')}>
          {errorMsg}
        </Alert>
      )}

      {isOnBreak && (
        <Alert severity="warning" sx={{ mb: 2.5 }}>
          You have an active break in progress. <strong>You must end your break before clocking out.</strong>
        </Alert>
      )}

      {/* Main body: Clocked In view vs Clocked Out view */}
      {isClockedIn ? (
        <Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
              gap: 2,
              p: 2,
              bgcolor: '#F9FAFB',
              borderRadius: '14px',
              border: '1px solid #F3F4F6',
              mb: 2.5,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Net Working Time
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827', fontFamily: 'monospace' }}>
                {formatDuration(netWorkingSeconds)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total Elapsed
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#4B5563', fontFamily: 'monospace' }}>
                {formatDuration(localElapsed)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Break Time
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: isOnBreak ? '#D97706' : '#6B7280',
                  fontFamily: 'monospace',
                }}
              >
                {formatDuration((session.completed_break_seconds || 0) + (isOnBreak ? localActiveBreak : 0))}
              </Typography>
            </Box>
          </Box>

          {/* Action buttons */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {isOnBreak ? (
              <Button
                variant="contained"
                color="warning"
                disabled={actionLoading}
                onClick={() => handlePunch('break-end')}
                startIcon={<CheckCircleOutlineIcon />}
                sx={{ flex: 1, py: 1.2, fontWeight: 700, borderRadius: '12px', textTransform: 'none' }}
              >
                {actionLoading ? 'Ending Break…' : 'End Break'}
              </Button>
            ) : (
              <Button
                variant="outlined"
                disabled={actionLoading}
                onClick={() => handlePunch('break-start')}
                startIcon={<FreeBreakfastIcon />}
                sx={{ flex: 1, py: 1.2, fontWeight: 700, borderRadius: '12px', textTransform: 'none' }}
              >
                {actionLoading ? 'Starting Break…' : 'Start Break'}
              </Button>
            )}

            <Tooltip title={isOnBreak ? 'You must end your active break before clocking out' : ''}>
              <span>
                <Button
                  variant="contained"
                  color="error"
                  disabled={actionLoading || isOnBreak}
                  onClick={() => handlePunch('clock-out')}
                  startIcon={<StopIcon />}
                  sx={{
                    flex: 1,
                    py: 1.2,
                    fontWeight: 700,
                    borderRadius: '12px',
                    textTransform: 'none',
                    bgcolor: '#EF4444',
                    '&:hover': { bgcolor: '#DC2626' },
                  }}
                >
                  {actionLoading ? 'Clocking Out…' : 'Clock Out'}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2.5 }}>
            Ready to begin your work day? Click below to record your clock-in.
          </Typography>
          <Button
            variant="contained"
            disabled={actionLoading}
            onClick={() => handlePunch('clock-in')}
            startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
            sx={{
              py: 1.4,
              px: 4,
              fontWeight: 800,
              fontSize: '1rem',
              borderRadius: '14px',
              textTransform: 'none',
              background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
              boxShadow: '0 4px 14px rgba(47,107,255,0.3)',
            }}
          >
            {actionLoading ? 'Clocking In…' : 'Clock In'}
          </Button>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
        Web attendance sessions record transactional browser evidence. Authoritative attendance, leave balances, and payroll calculations remain separate.
      </Typography>

      <WebRegularizationModal
        open={regModalOpen}
        onClose={() => setRegModalOpen(false)}
        onSuccess={() => {
          fetchStatus();
          if (onActionComplete) onActionComplete();
        }}
        initialSessionId={session?.id}
      />
    </Paper>
  );
}
